import { useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "../config";
import { usePageMeta } from "../seo";
import {
  CheckIcon,
  SparkleIcon,
  PlusMinusIcon,
  ArrowRightIcon,
  ShieldIcon,
} from "../components/Icons";
import { DotPattern } from "../components/Patterns";

const included = [
  "Email: forwarding, IMAP, Gmail and Microsoft 365",
  "WhatsApp (Meta Cloud API) and phone via Acefone",
  "Saved replies, rules, auto-close and SLA targets",
  "Satisfaction surveys and reports",
  "AI drafts and summaries with your own OpenAI or Anthropic key",
  "Shopify and Zopping store sidebars",
  "Customer data lookup, ticket actions, API and webhooks",
  "As many agents and tickets as you need during the beta",
];

const faq = [
  {
    q: "Is it really free?",
    a: "Yes, for as long as ZopDesk is in beta. There's no card to enter and nothing to cancel.",
  },
  {
    q: "What happens when the beta ends?",
    a: "We'll publish pricing before launch and tell every team using ZopDesk ahead of time, so you can decide whether to stay.",
  },
  {
    q: "What about AI costs?",
    a: "AI drafts and summaries go through your own OpenAI or Anthropic account with your API key, so your provider bills you for those directly. ZopDesk doesn't add anything on top.",
  },
  {
    q: "Are there limits during the beta?",
    a: "There are no plan limits on agents or tickets. A few technical limits apply to everyone, for example attachments are capped at 10 MB per file and an org can have up to 10 ticket actions.",
  },
];

export default function Pricing() {
  usePageMeta("/pricing");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-10 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            Pricing
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl"
            style={{ animationDelay: "60ms" }}
          >
            Free during the beta.
          </h1>
          <p
            className="mt-4 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            ZopDesk is in beta, and every feature is free to use while it is.
            We'll announce pricing before launch, with notice to every team
            using it.
          </p>
          <div
            className="mt-6 flex animate-fade-up flex-wrap items-center gap-5 text-xs text-ink-400"
            style={{ animationDelay: "220ms" }}
          >
            <span className="inline-flex items-center gap-1.5">
              <CheckIcon width={14} height={14} className="text-emerald-400" />
              No credit card
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckIcon width={14} height={14} className="text-emerald-400" />
              Every feature included
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckIcon width={14} height={14} className="text-emerald-400" />
              Notice before pricing starts
            </span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-ink-900 bg-ink-900 p-6 text-white shadow-xl sm:p-10">
          <div className="pointer-events-none absolute inset-0 text-white/[0.05]">
            <DotPattern />
          </div>
          <div className="relative grid gap-10 md:grid-cols-[1fr_1.3fr]">
            <div>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white">
                <SparkleIcon width={12} height={12} />
                Beta
              </span>
              <h2 className="mt-4 text-2xl font-semibold">Everything, for now</h2>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-5xl font-semibold tracking-tight">$0</span>
                <span className="text-sm text-ink-300">during the beta</span>
              </div>
              <a
                href={SITE.appUrl}
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-ink-100"
              >
                Start free
                <ArrowRightIcon width={14} height={14} />
              </a>
            </div>
            <ul className="space-y-3 text-sm">
              {included.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-500/20 text-brand-200"
                  >
                    <CheckIcon width={12} height={12} strokeWidth={2.4} />
                  </span>
                  <span className="text-ink-100">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            Pricing FAQ
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            The questions people ask.
          </h2>
        </div>
        <div className="divide-y divide-ink-200 overflow-hidden rounded-xl border border-ink-200 bg-white">
          {faq.map((f, i) => (
            <FaqItem key={f.q} index={i} {...f} />
          ))}
        </div>
        <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl border border-ink-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100">
              <ShieldIcon width={22} height={22} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink-900">Something else on your mind?</h3>
              <p className="mt-1 max-w-xl text-sm text-ink-600">
                Ask us about the beta, the roadmap or how your data is handled.
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            className="rounded-md border border-ink-200 px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-ink-50"
          >
            Talk to us
          </Link>
        </div>
      </section>
    </>
  );
}

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left transition hover:bg-ink-50"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-ink-900">{q}</span>
        <span
          className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border border-ink-200 text-ink-600 transition ${
            open ? "rotate-45 border-brand-200 bg-brand-50 text-brand-700" : ""
          }`}
        >
          <PlusMinusIcon width={14} height={14} />
        </span>
      </button>
      <div
        className={`grid overflow-hidden px-6 transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-3xl text-sm leading-relaxed text-ink-600">{a}</p>
        </div>
      </div>
    </div>
  );
}
